import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import highchartsAccessibility from "highcharts/modules/accessibility";
import highchartsExporting from "highcharts/modules/exporting";
import highchartsOfflineExporting from "highcharts/modules/offline-exporting";
import { IntlContext } from "context";
import { exportCharts } from "helpers/highcharts/export";
import { createDiagramSeries, getDiagramGroupBy } from "helpers/highcharts/getDiagramCase";
import produitDataFetcher from "dataProvider/Produit";
import exportDataFetcher from "dataProvider/Export";
import BackToId from "ui/Button/BackToId";
import Loader from "ui/Loader";
import PagerNav from "ui/List/PagerNav";
import Warnings from "../Common/Warnings";
import { matomoPush } from "../../Produit/helpers";
import {
    GRAPHES_PAR_PAGE,
    PlanGroupe,
    cleDeGroupe,
    groupesDeLaPage,
    lastPage,
    totalCharts,
    construirePlan,
} from "./pagePlan";

// Nécessaire pour ce type de graph
HighchartsMore(Highcharts);
highchartsAccessibility(Highcharts);
highchartsExporting(Highcharts);
highchartsOfflineExporting(Highcharts);

const maxBoxplotDisplay = 100;

type ChartProps = {
    chartOptions: Highcharts.Options;
};

const Chart: React.FC<ChartProps> = ({ chartOptions }) => {
    return (
        <div className="custom-chart">
            <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
                containerProps={{ style: { height: "100%", width: "100%" } }}
            />
        </div>
    );
};

type DatableHeaders = {
    code: string;
    label: string;
    effectif: string;
};

const GenerateDatable: React.FC<{ data: any; headers: DatableHeaders }> = ({ data, headers }) => {
    if (!data?.exportData) {
        return <></>;
    }

    return (
        <table className="stats-table-alimentaire">
            <thead className="row-group">
                <tr className="header-row">
                    <th className="header-cell">{data.exportData?.header?.code || headers.code}</th>
                    <th className="header-cell">{data.exportData?.header?.label || headers.label}</th>
                    <th className="header-cell">{data.exportData?.header?.effectif || headers.effectif}</th>
                </tr>
            </thead>
            <tbody>
                {data.exportData.labels.map((categorie: any, index: number) => {
                    const result = categorie.match(/^(.*)<br\/>n: (\d+)$/);
                    return (
                        <tr key={index}>
                            <td className="cell">{index + 1}</td>
                            <td className="cell">{result[1]}</td>
                            <td className="cell">{result[2]}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

/**
 * Met à plat les configurations de graphes produites par createDiagramSeries.
 */
const flattenSeries = (series: any[]): Highcharts.Options[] => {
    if (!series.length) {
        return [];
    }

    return Array.isArray(series[0]) ? ([] as Highcharts.Options[]).concat(...series) : series;
};

/**
 * Construit les graphes de chaque groupe demandé.
 *
 * createDiagramSeries est appelé sur les lignes d'un seul groupe à la fois :
 * l'échelle Y, la légende d'unités et le découpage en tranches de 26 boxplots
 * se calculent par groupe, le résultat est donc identique à une construction
 * sur la sélection entière.
 */
const construireParGroupe = (
    activeFilters: any,
    reponse: any,
    groupes: PlanGroupe[]
): Record<string, Highcharts.Options[]> => {
    const lignes: any[] = Array.from(reponse || []);
    const resultat: Record<string, Highcharts.Options[]> = {};

    groupes.forEach((groupe) => {
        const duGroupe = lignes.filter((ligne: any) => {
            if (Number(ligne.nutriments[0].id_constituant_std) !== groupe.nutriment) {
                return false;
            }
            if (groupe.secteur !== null && Number(ligne.secteur_id) !== groupe.secteur) {
                return false;
            }
            if (groupe.famille !== null && Number(ligne.famille_id) !== groupe.famille) {
                return false;
            }
            return true;
        });

        resultat[cleDeGroupe(groupe)] = flattenSeries(createDiagramSeries(activeFilters, duGroupe));
    });

    return resultat;
};

const Diagram: React.FC<{ activeFilters: any; filtersMax: any }> = ({ activeFilters, filtersMax }) => {
    const intl = useContext(IntlContext);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [plan, setPlan] = useState<PlanGroupe[]>([]);
    // Distingue « plan pas encore reçu » de « plan reçu et vide » : sans ça une
    // sélection sans résultat resterait bloquée sur le chargement.
    const [planRecu, setPlanRecu] = useState<boolean>(false);
    // Avertissements de la sélection, portés par la réponse du plan : c'est le
    // seul appel toujours émis, y compris quand aucun graphe n'en sort.
    const [avertissements, setAvertissements] = useState<Array<string>>([]);
    const [chartsParGroupe, setChartsParGroupe] = useState<Record<string, Highcharts.Options[]>>({});
    const [page, setPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [erreur, setErreur] = useState<boolean>(false);
    const activeFiltersJson = JSON.stringify(activeFilters);
    const requetes = useRef<any[]>([]);

    /**
     * Les filtres partent tels quels, comme avant la pagination.
     *
     * Vider filter_nutriment quand tout est coché ferait gagner moins d'une
     * seconde sur la sélection la plus large, mais ce n'est pas neutre : la liste
     * des modalités écarte les constituants inactifs (ConstituantStd/Liste), pas
     * la requête du diagramme. Retirer le whereIn ferait donc entrer des
     * nutriments que le panneau de filtres n'offre pas.
     */
    const filtresPourApi = useCallback(() => JSON.parse(activeFiltersJson), [activeFiltersJson]);

    // Nouvelle sélection : on repart du plan de pagination.
    useEffect(() => {
        let annule = false;

        setPlan([]);
        setPlanRecu(false);
        setAvertissements([]);
        setErreur(false);
        setChartsParGroupe({});
        setPage(1);
        setIsLoading(true);

        const groupBy = getDiagramGroupBy(JSON.parse(activeFiltersJson));
        const requete = produitDataFetcher.statsDiagramPlan(
            Object.assign({}, filtresPourApi(), { filter_diagram: true })
        );
        requetes.current.push(requete);

        requete
            .then((reponse: any) => {
                if (!annule) {
                    setPlan(construirePlan(reponse, groupBy));
                    setAvertissements(reponse?.warnings || []);
                    setPlanRecu(true);
                }
            })
            .catch((err: any) => {
                if (!annule) {
                    console.error(err);
                    setErreur(true);
                    setIsLoading(false);
                }
            });

        return () => {
            annule = true;
            requetes.current.forEach((r) => r?.cancel?.());
            requetes.current = [];
        };
    }, [activeFiltersJson, filtresPourApi]);

    // Charge les groupes de la page courante qui ne sont pas déjà en cache.
    useEffect(() => {
        if (!planRecu) {
            return;
        }

        // Plan reçu mais vide : la sélection ne donne aucun graphe, on rend la main.
        if (!plan.length) {
            setIsLoading(false);
            return;
        }

        // Le plan donne le volume total avant de charger les données détaillées.
        // On conserve la pagination pour les volumes acceptables, mais on évite
        // toute génération de graphe au-delà de la limite historique.
        if (totalCharts(plan) > maxBoxplotDisplay) {
            setIsLoading(false);
            return;
        }

        let annule = false;
        const { groupes } = groupesDeLaPage(plan, page);
        const manquants = groupes.filter((groupe) => !chartsParGroupe[cleDeGroupe(groupe)]);

        if (!manquants.length) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const requete = produitDataFetcher.stats(
            Object.assign({}, filtresPourApi(), {
                filter_diagram: true,
                filter_diagram_groups: manquants.map((groupe) => ({
                    secteur: groupe.secteur,
                    famille: groupe.famille,
                    nutriment: groupe.nutriment,
                })),
            })
        );
        requetes.current.push(requete);

        requete
            .then((reponse: any) => {
                if (annule) {
                    return;
                }
                setChartsParGroupe((precedent) =>
                    Object.assign({}, precedent, construireParGroupe(activeFilters, reponse, manquants))
                );
                setIsLoading(false);
            })
            .catch((err: any) => {
                if (!annule) {
                    console.error(err);
                    setErreur(true);
                    setIsLoading(false);
                }
            });

        return () => {
            annule = true;
        };
    }, [plan, planRecu, page, chartsParGroupe, activeFilters, filtresPourApi]);

    // Une vue par sélection, pas une par page
    useEffect(() => {
        matomoPush([
            ["setReferrerUrl", window.location.href],
            ["setCustomUrl", `${window.location.href}/composition_nutritionnelle`],
        ]);
    }, [activeFiltersJson]);

    const datableHeaders: DatableHeaders = {
        code: intl.formatMessage("offre_alimentaire.diagram.header.code"),
        label: intl.formatMessage("offre_alimentaire.diagram.header.label"),
        effectif: intl.formatMessage("offre_alimentaire.diagram.header.effectif"),
    };

    /**
     * Export local : la page affichée ne contient qu'une partie des graphes, on
     * redemande donc la sélection complète — sans filter_diagram_groups — et on
     * rend les graphes hors DOM.
     */
    const exportLocaly = () => {
        setDownloading(true);
        produitDataFetcher
            .stats(Object.assign({}, filtresPourApi(), { filter_diagram: true }))
            .then((fullData: any) => {
                const flat = flattenSeries(createDiagramSeries(activeFilters, fullData));

                const holder = document.createElement("div");
                holder.style.position = "absolute";
                holder.style.left = "-10000px";
                holder.style.width = "800px";
                document.body.appendChild(holder);

                const exported = flat.map((options: Highcharts.Options) => {
                    const container = document.createElement("div");
                    holder.appendChild(container);
                    return Highcharts.chart(container, options);
                });

                exportCharts(exported, flat, datableHeaders);

                setTimeout(() => {
                    exported.forEach((chart: Highcharts.Chart) => chart.destroy());
                    holder.parentNode?.removeChild(holder);
                }, 30000);
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => setDownloading(false));
    };

    const exportDiagram = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            event.preventDefault();
            setDownloading(true);
            matomoPush([
                [
                    "trackEvent",
                    "Export outil de visualisation",
                    "Composition nutritionnelle",
                    JSON.stringify(activeFilters?.matomo),
                    1,
                ],
            ]);

            if (
                filtersMax.nutriment === activeFilters.filter_nutriment.length &&
                0 === activeFilters.filter_famille.length &&
                filtersMax.annee === activeFilters.filter_annee.length &&
                filtersMax.secteur === activeFilters.filter_secteur.length
            ) {
                if (filtersMax.typeMarque === activeFilters.filter_type_marque.length) {
                    const promise = exportDataFetcher.getSelectedArchiveFromDiagram({
                        filename: "full_export_with_brand.zip",
                    });
                    promise
                        .then((data: any) => {
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data.content);
                            link.download = data.name;
                            link.click();
                            link.parentNode?.removeChild(link);
                        })
                        .catch(() => {
                            exportLocaly();
                        })
                        .finally(() => setDownloading(false));

                    return;
                } else if (activeFilters.filter_type_marque.length === 0) {
                    // export diagram without type marque
                    const promise = exportDataFetcher.getSelectedArchiveFromDiagram({
                        filename: "full_export.zip",
                    });
                    promise
                        .then((data: any) => {
                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(data.content);
                            link.download = data.name;
                            link.click();
                            link.parentNode?.removeChild(link);
                        })
                        .catch(() => {
                            exportLocaly();
                        })
                        .finally(() => setDownloading(false));
                    return;
                }
            }
            exportLocaly();
        } catch (e) {
            console.error(e);
            setDownloading(false);
        }
    };

    const exportButton = () => {
        return (
            <div className="container--export">
                <button
                    type="button"
                    className="cta cta--secondary"
                    title={intl.formatMessage("generic.export")}
                    disabled={downloading}
                    onClick={(e) => exportDiagram(e)}
                >
                    {downloading
                        ? intl.formatMessage("generic.loader.loading.label")
                        : intl.formatMessage("generic.export")}
                </button>
            </div>
        );
    };

    if (isLoading) {
        return <Loader message={intl.formatMessage("generic.loader.loading.label")} />;
    }

    const total = totalCharts(plan);

    if (total > maxBoxplotDisplay) {
        return (
            <>
                <p>{intl.formatMessage("offre_alimentaire.diagram.too_many")}</p>
                <Warnings warnings={avertissements} />
            </>
        );
    }

    // Requête en échec : sans ça la page afficherait « Graphiques 1 – 0 / n »
    // sans un seul graphe et sans explication.
    if (erreur || !plan.length || !total) {
        return (
            <>
                <div className="stats-container-top">
                    <span className="results">{intl.formatMessage("produit.label.no_result")}</span>
                </div>
                <Warnings warnings={avertissements} />
            </>
        );
    }

    const enteteResultats = (): string => {
        const dernierePage = lastPage(plan);
        const libelle = intl.formatMessage(
            total > 1 ? "offre_alimentaire.diagram.charts" : "offre_alimentaire.diagram.chart"
        );

        if (dernierePage <= 1) {
            return `${total} ${libelle}`;
        }

        return `${total} ${libelle} — ${intl.formatMessage("offre_alimentaire.diagram.page", {
            page: String(page),
            total: String(dernierePage),
        })}`;
    };

    const { groupes, premierGraphe } = groupesDeLaPage(plan, page);
    const chartsDeLaPage = groupes
        .reduce(
            (tous: Highcharts.Options[], groupe) => tous.concat(chartsParGroupe[cleDeGroupe(groupe)] || []),
            []
        )
        .slice(premierGraphe, premierGraphe + GRAPHES_PAR_PAGE);

    const premierNumero = (page - 1) * GRAPHES_PAR_PAGE + 1;

    return (
        <>
            {exportButton()}

            <Warnings warnings={avertissements} />

            <div className="stats-container-top">
                <span className="results">{enteteResultats()}</span>
            </div>

            {chartsDeLaPage.map((configDiagram: Highcharts.Options, k: number) => (
                <div key={premierNumero + k}>
                    <Chart chartOptions={configDiagram} />
                    <div className="highcharts-credits">
                        {intl.formatMessage("produit.link.credits", { date: new Date().toLocaleDateString("fr") })}
                    </div>
                    <GenerateDatable data={configDiagram} headers={datableHeaders} />
                </div>
            ))}

            <PagerNav
                currentPage={page}
                lastPage={lastPage(plan)}
                onChange={(selected: number) => {
                    setPage(selected);
                    document.getElementById("pageup")?.scrollIntoView();
                }}
            />

            {exportButton()}

            <BackToId label={intl.formatMessage("generic.go_to.top")} toId="pageup" />
        </>
    );
};

export default Diagram;
